import React, { useState, useContext, useCallback } from 'react';
import classnames from 'classnames';
import Flex from '@wix/da-ds/pkg/Flex';
import {
  ThemeOverride,
  ThemeSurface,
} from '@wix/da-react-context/pkg/ThemeContext';
import ScrollableContent from '@wix/da-ds/pkg/ScrollableContent';
import MediaSourceMenu from '../MediaSourceMenu/Mobile';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useCurrentGallection } from '../../_hocs/withCurrentGallection';
import Breadcrumbs from '../Breadcrumbs';
import UploadButton from '../UploadButton';
import { useFileUpload } from '../../_hocs/withFileUpload';

import s from './PopupInnerLayout.scss';

const ON_SCROLL_BOTTOM_DISTANCE = 200;

export interface Props {
  headerSubtitle?: string;
  headerExtrasRightZone?: React.ReactElement<any>;
  headerExtrasBottomZone?: React.ReactNode;
  scrollableContent: React.ReactNode;
  sideBarContent?: React.ReactNode;
  onScrolledToBottom?: () => void;
  onNotScrollable?: () => void;
  oneRowHeader?: boolean;
}

const PopupInnerLayout: React.FC<Props> = ({
  headerSubtitle,
  headerExtrasRightZone,
  headerExtrasBottomZone,
  scrollableContent,
  sideBarContent,
  onScrolledToBottom,
  onNotScrollable,
  oneRowHeader,
}) => {
  const isMobile = useContext(MobileContext);
  const { allowUploads = true } = useFileUpload();
  const { currentFolderId } = useCurrentGallection();
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);

  const onScrolled = useCallback(
    (scrollTop: number, distanceToBottom: number) => {
      const newIsHeaderSticky = scrollTop !== 0;
      setIsHeaderSticky(newIsHeaderSticky);
      if (distanceToBottom <= ON_SCROLL_BOTTOM_DISTANCE) {
        onScrolledToBottom && onScrolledToBottom();
      }
    },
    [onScrolledToBottom]
  );

  const handleNotScrollable = useCallback(() => {
    onNotScrollable && onNotScrollable();
  }, [onNotScrollable]);

  const renderSubtitle = () => {
    if (!headerSubtitle) {
      return null;
    }
    return <div className={s['header-subtitle']}>{headerSubtitle}</div>;
  };

  const renderMobileHeader = () => {
    return (
      <div className={s['header-top-row']}>
        {oneRowHeader && renderSubtitle()}
        <div className={s['header-bottom-row']}>
          {currentFolderId ? <Breadcrumbs /> : <MediaSourceMenu />}
          {headerExtrasRightZone}
        </div>
        {allowUploads && <UploadButton />}
      </div>
    );
  };

  const renderDesktopHeader = () => {
    return (
      <div className={s['header-top-row']}>
        <Breadcrumbs />
        {oneRowHeader && renderSubtitle()}
        <div className={s['header-extras-right-zone']}>
          {headerExtrasRightZone}
        </div>
      </div>
    );
  };

  const renderHeader = () => {
    return (
      <>
        <div className={classnames(s['header'], isHeaderSticky && s['sticky'])}>
          <ThemeOverride
            themeSurface={ThemeSurface.SECONDARY}
            className={s['header-theme-wrapper']}
          >
            {isMobile ? renderMobileHeader() : renderDesktopHeader()}
            {!oneRowHeader && renderSubtitle()}
            {!isMobile && headerExtrasBottomZone}
          </ThemeOverride>
        </div>
        {isMobile && headerExtrasBottomZone}
      </>
    );
  };

  return (
    <Flex className={s['root']}>
      <Flex fullWidth direction="column">
        {renderHeader()}
        <ScrollableContent
          className={s['scrollable-content']}
          onScrolled={onScrolled}
          onNotScrollable={handleNotScrollable}
        >
          <ThemeOverride themeSurface={ThemeSurface.SECONDARY}>
            {scrollableContent}
          </ThemeOverride>
        </ScrollableContent>
      </Flex>
      {sideBarContent}
    </Flex>
  );
};

export default PopupInnerLayout;
